import React, { useEffect, useRef, useState } from "react";
import { onValue, ref as dbRef } from "firebase/database";
import { refreshBuilderCriticalFacilities } from "../../../helpers/AI_functions";
import "./MBCriticalFacilitiesPanel.css";

const makeBlockId = (simulationId, layerId) => {
  const suffix = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${simulationId}_${layerId}_critical_facilities_${suffix}`;
};

const normalizeMessages = (value) => {
  if (value == null) return [];
  if (typeof value === "string") return [{ id: "latest", text: value }];
  if (typeof value !== "object") return [{ id: "latest", text: String(value) }];

  return Object.entries(value)
    .map(([id, item]) => ({
      id,
      text: String(item?.message ?? item?.text ?? item ?? ""),
    }))
    .sort((a, b) => Number(a.id) - Number(b.id));
};

export default function MBCriticalFacilitiesPanel({
  database,
  simulationId,
  layerId,
  layerName,
  onCompleted,
}) {
  const [blockId, setBlockId] = useState("");
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const completedBlocksRef = useRef(new Set());

  useEffect(() => {
    if (!database || !blockId) return undefined;

    const messagesRef = dbRef(database, `model_builders/blocks/${blockId}/message`);
    return onValue(
      messagesRef,
      (snapshot) => {
        const nextMessages = normalizeMessages(snapshot.val());
        setMessages(nextMessages);
        const latest = String(nextMessages[nextMessages.length - 1]?.text || "").trim();

        if (/^error:/i.test(latest)) {
          setStatus("failed");
          setError(latest.replace(/^error:\s*/i, "") || "Critical-facilities import failed.");
          return;
        }

        if (latest.toLowerCase() !== "completed" || completedBlocksRef.current.has(blockId)) {
          return;
        }

        completedBlocksRef.current.add(blockId);
        setStatus("refreshing");
        Promise.resolve(onCompleted?.())
          .then(() => setStatus("completed"))
          .catch((refreshError) => {
            setStatus("failed");
            setError(
              `The import completed, but the parcel layer could not be reloaded: ${String(
                refreshError?.message || refreshError
              )}`
            );
          });
      },
      (subscriptionError) => {
        setStatus("failed");
        setError(String(subscriptionError?.message || subscriptionError));
      }
    );
  }, [database, blockId, onCompleted]);

  const startImport = async () => {
    if (!simulationId || !layerId) return;
    const nextBlockId = makeBlockId(simulationId, layerId);
    setBlockId(nextBlockId);
    setMessages([]);
    setError("");
    setStatus("submitting");

    try {
      await refreshBuilderCriticalFacilities({
        simulationId,
        layerId,
        blockId: nextBlockId,
      });
      setStatus((current) => (
        current === "submitting" ? "running" : current
      ));
    } catch (submissionError) {
      setStatus("failed");
      setError(String(submissionError?.message || submissionError));
    }
  };

  const isBusy = status === "submitting" || status === "running" || status === "refreshing";
  const buttonText = status === "submitting"
    ? "Submitting..."
    : status === "refreshing"
      ? "Reloading layer..."
      : status === "running"
        ? "Importing..."
        : "Import (OSM)";

  return (
    <section className="mb-criticalFacilities" aria-labelledby="mb-criticalFacilities-title">
      <div className="mb-criticalFacilities-header">
        <h3 id="mb-criticalFacilities-title">Critical facilities</h3>
        <p>
          Fetch and match OSM critical facilities into {layerName || "this parcel layer"}.
          The existing layer CSV will be updated.
        </p>
      </div>

      {blockId ? <div className="mb-criticalFacilities-blockId">Operation: {blockId}</div> : null}
      {messages.length ? (
        <div className="mb-criticalFacilities-progress" aria-live="polite">
          {messages.map((message) => (
            <div key={message.id}>• {message.text}</div>
          ))}
        </div>
      ) : null}
      {status === "completed" ? (
        <div className="mb-criticalFacilities-message isSuccess">Import completed and the parcel layer was reloaded.</div>
      ) : null}
      {error ? <div className="mb-criticalFacilities-message isError">{error}</div> : null}
      <div className="mb-criticalFacilities-actions">
        <button className="mb-btn" type="button" disabled={isBusy || !layerId} onClick={startImport}>
          {buttonText}
        </button>
      </div>
    </section>
  );
}
